import { TrendingUp, TrendingDown, Minus, Save, ArrowRight } from "lucide-react";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

/**
 * Sticky Starting / Closing / Difference summary bar.
 */
export function BalanceSummaryBar({
  startingTotal = 0,
  closingTotal = 0,
  difference = 0,
  startingBreakdown = null,
  closingBreakdown = null,
  onSaveClosing = null,
  isSaving = false,
  lastSavedLabel = null,
}) {
  const diffPositive = difference > 0;
  const diffNegative = difference < 0;
  const balanced = difference === 0;

  return (
    <div className="sticky top-20 z-30 mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4 md:items-stretch">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
              1
            </span>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-800">
              Starting
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ${money(startingTotal)}
          </p>
          {startingBreakdown}
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
              2
            </span>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-800">
              Closing
            </p>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ${money(closingTotal)}
          </p>
          {closingBreakdown}
        </div>

        <div
          className={`rounded-lg border px-4 py-3 ${
            balanced
              ? "border-gray-200 bg-gray-50"
              : diffPositive
                ? "border-emerald-300 bg-emerald-50"
                : "border-red-300 bg-red-50"
          }`}
        >
          <div className="mb-1 flex items-center gap-2">
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                balanced
                  ? "bg-gray-500"
                  : diffPositive
                    ? "bg-emerald-600"
                    : "bg-red-600"
              }`}
            >
              Δ
            </span>
            <p className="text-sm font-semibold uppercase tracking-wide text-gray-700">
              Difference
            </p>
          </div>
          <p
            className={`flex items-center gap-1.5 text-2xl font-bold ${
              balanced
                ? "text-gray-700"
                : diffPositive
                  ? "text-emerald-700"
                  : "text-red-700"
            }`}
          >
            {diffPositive ? (
              <TrendingUp className="h-5 w-5" />
            ) : diffNegative ? (
              <TrendingDown className="h-5 w-5" />
            ) : (
              <Minus className="h-5 w-5" />
            )}
            {diffPositive ? "+" : ""}
            ${money(difference)}
          </p>
          <p
            className={`mt-1 text-xs font-semibold ${
              balanced
                ? "text-gray-500"
                : diffPositive
                  ? "text-emerald-700"
                  : "text-red-700"
            }`}
          >
            {balanced
              ? "Balanced"
              : diffPositive
                ? `Surplus $${money(Math.abs(difference))}`
                : `Deficit $${money(Math.abs(difference))}`}
          </p>
        </div>

        <div className="flex flex-col justify-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50/80 p-3">
          {onSaveClosing && (
            <button
              type="button"
              onClick={onSaveClosing}
              disabled={isSaving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-yellow-400 px-4 py-3 text-sm font-semibold text-black transition hover:bg-yellow-500 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Saving…" : "Save Closing"}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {lastSavedLabel && (
            <p className="text-center text-[11px] font-medium text-gray-500">
              {lastSavedLabel}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
